import classNames from "classnames";
import Link from "next/link";
import type { ReactNode } from "react";
import React from "react";

type Props = {
  children: ReactNode;
  className?: string;
  containerClassName?: string;
  /** Optional security note shown above the Quantasuite powered-by line. */
  securityNote?: string;
  brand: {
    address: string | null;
    headerColor: string | null;
    name: string;
    phone: string | null;
    logo: string | null;
    showBrandLogoOnLink?: boolean;
  };
};

const DEFAULT_SECURITY_NOTE = "Secured using TLS & SSL encryption.";
const POWERED_BY_HREF = "https://quantasuite.com/";
const POWERED_BY_NAME = "Quantasuite";

function SecurityShieldIcon() {
  return (
    <svg width="17" height="17" viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M8.161 0.23701C8.25629 0.157973 8.3762 0.114716 8.5 0.114716C8.6238 0.114716 8.74371 0.157973 8.839 0.23701C10.8366 1.89867 13.3215 2.86376 15.917 2.98601C16.034 2.98961 16.1459 3.03411 16.2335 3.11177C16.321 3.18942 16.3785 3.29532 16.396 3.41101C16.465 3.93101 16.5 4.46101 16.5 5.00101C16.5 10.163 13.24 14.564 8.666 16.257C8.55886 16.2965 8.44114 16.2965 8.334 16.257C3.76 14.564 0.5 10.163 0.5 5.00001C0.5 4.46201 0.535 3.93101 0.604 3.41101C0.621528 3.29515 0.67919 3.18913 0.766919 3.11145C0.854649 3.03378 0.966873 2.98938 1.084 2.98601C3.67927 2.86326 6.16379 1.89882 8.161 0.23701ZM12.357 6.19101C12.474 6.03002 12.5222 5.82917 12.491 5.63263C12.4599 5.43609 12.352 5.25997 12.191 5.14301C12.03 5.02605 11.8292 4.97783 11.6326 5.00896C11.4361 5.04009 11.26 5.14802 11.143 5.30901L7.66 10.099L5.78 8.21901C5.71078 8.14741 5.628 8.09031 5.53647 8.05105C5.44495 8.01179 5.34653 7.99115 5.24694 7.99033C5.14736 7.98951 5.04861 8.00853 4.95646 8.04629C4.8643 8.08404 4.78059 8.13977 4.7102 8.21022C4.63982 8.28067 4.58417 8.36444 4.5465 8.45663C4.50883 8.54882 4.4899 8.64759 4.49081 8.74717C4.49173 8.84675 4.51246 8.94516 4.55181 9.03664C4.59116 9.12813 4.64834 9.21086 4.72 9.28001L7.22 11.78C7.29663 11.8567 7.38896 11.9158 7.49065 11.9534C7.59233 11.9909 7.70094 12.006 7.80901 11.9975C7.91708 11.9891 8.02203 11.9573 8.11663 11.9044C8.21123 11.8515 8.29324 11.7787 8.357 11.691L12.357 6.19101Z"
        fill="#10B981"
      />
    </svg>
  );
}

export default function BrandedComponentWrapper({
  children,
  className,
  containerClassName,
  securityNote = DEFAULT_SECURITY_NOTE,
  brand,
}: Props) {
  return (
    <div className="flex w-full flex-col items-center justify-center">
      <main
        className={classNames(
          "mx-auto flex w-full flex-col items-center overflow-hidden rounded-br-md rounded-bl-md border border-t-0 border-gray-200 ease-in-out md:mx-4 md:w-[1000px] md:max-w-[1000px]",
          className
        )}
        style={{ backgroundColor: brand.headerColor || "#000" }}>
        <div className="w-full px-3 py-3 md:px-10" style={{ backgroundColor: brand.headerColor || "#000" }}>
          <p className="text-left text-sm leading-6 text-white">{brand.address}</p>
        </div>
        <div className="flex w-full flex-col items-center justify-between gap-2 rounded-t-xl border-b bg-[#FAFBFD] px-8 py-4 md:flex-row md:gap-0">
          <div className="flex w-full flex-col items-center justify-center gap-2 px-2 md:flex-row md:flex-wrap md:justify-between md:gap-0">
            <div className="rounded bg-white py-1.5">
              <img alt="logo" className="mx-auto h-16 w-auto p-1" src={brand.logo || ""} />
            </div>
            {brand.showBrandLogoOnLink && (
              <div className="flex flex-col items-center gap-1 text-center md:items-end md:text-right">
                <p className="text-base font-bold capitalize text-gray-800">{brand.name}</p>
                <p className="text-sm leading-6 text-gray-600">{brand.phone}</p>
              </div>
            )}
          </div>
        </div>
        <div
          className={classNames(
            "flex min-h-[500px] w-full flex-col bg-white md:rounded-bl-md md:rounded-br-md",
            containerClassName
          )}>
          {children}
        </div>
      </main>
      <div className="mx-auto my-10 flex w-full flex-col items-center justify-center gap-3 md:w-3/4">
        <div className="flex flex-col items-center gap-2 md:flex-row">
          <SecurityShieldIcon />
          <p className="text-sm leading-6 text-gray-600">{securityNote}</p>
        </div>
        <p className="text-sm leading-6 text-gray-600">
          Powered by{" "}
          <Link href={POWERED_BY_HREF} className="underline">
            {POWERED_BY_NAME}
          </Link>
          .
        </p>
      </div>
    </div>
  );
}
